import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { map, type Observable } from 'rxjs';

import { API_BASE_URL } from '../config/api-base-url.token';
import type { BrandListResponse } from '../models/brand.models';
import type {
  PublicTemplateConfigSaveResponse,
  PublicTemplateConfigSessionResponse,
  PublicTemplateConfigUrlResponse,
  TemplateListResponse,
  TemplatePayload,
  TemplateSummary,
  UploadedPdfFile,
} from '../models/template.models';

@Injectable({ providedIn: 'root' })
export class TemplateApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  uploadPdf(file: File): Observable<UploadedPdfFile> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    return this.http.post<UploadedPdfFile>(`${this.baseUrl}/files/pdf`, formData);
  }

  downloadPdf(fileId: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/files/pdf/${fileId}`, {
      responseType: 'blob',
    });
  }

  createTemplate(payload: TemplatePayload): Observable<TemplateSummary> {
    return this.http.post<TemplateSummary>(`${this.baseUrl}/templates`, payload);
  }

  listTemplatesPage(page = 1, limit = 20, search = ''): Observable<TemplateListResponse> {
    return this.http.get<TemplateListResponse>(`${this.baseUrl}/templates`, {
      params: {
        page,
        limit,
        search,
      },
    });
  }

  listTemplates(page = 1, limit = 20, search = ''): Observable<TemplateSummary[]> {
    return this.listTemplatesPage(page, limit, search).pipe(map((response) => response.items));
  }

  getTemplate(id: string): Observable<TemplateSummary> {
    return this.http.get<TemplateSummary>(`${this.baseUrl}/templates/${id}`);
  }

  updateTemplate(id: string, payload: Partial<TemplatePayload>): Observable<TemplateSummary> {
    return this.http.patch<TemplateSummary>(`${this.baseUrl}/templates/${id}`, payload);
  }

  deleteTemplate(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/templates/${id}`);
  }

  getPublicTemplateConfigSession(token: string): Observable<PublicTemplateConfigSessionResponse> {
    return this.http.get<PublicTemplateConfigSessionResponse>(
      `${this.baseUrl}/public/template-config/${encodeURIComponent(token)}`,
    );
  }

  getPublicTemplateConfigPdf(token: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/public/template-config/${encodeURIComponent(token)}/pdf`, {
      responseType: 'blob',
    });
  }

  getPublicTemplateConfigBrands(token: string): Observable<BrandListResponse> {
    return this.http.get<BrandListResponse>(`${this.baseUrl}/public/template-config/${encodeURIComponent(token)}/brands`);
  }

  savePublicTemplateConfigFields(
    token: string,
    payload: { fields: TemplatePayload['fields'] },
  ): Observable<PublicTemplateConfigSaveResponse> {
    return this.http.put<PublicTemplateConfigSaveResponse>(
      `${this.baseUrl}/public/template-config/${encodeURIComponent(token)}/fields`,
      payload,
    );
  }

  refreshPublicTemplateConfigUrl(templateId: string): Observable<PublicTemplateConfigUrlResponse> {
    return this.http.post<PublicTemplateConfigUrlResponse>(
      `${this.baseUrl}/public/templates/${encodeURIComponent(templateId)}/configure-fields-url`,
      {},
    );
  }
}
